import React from 'react';
import { Card, CardContent } from '@components/ui/card';
import { Separator } from '@components/ui/separator';
import { cn } from '@core/utils/cn';
import { EnrichedTrack, SelectedWorkout } from '../types';
import { fmt, fmtDuration, fmtKmh, fmtTime } from '../utils/format';

interface PointDetailsPanelProps {
    track: EnrichedTrack | null;
    workout: SelectedWorkout | null;
    progress: number;
}

type Tone = 'pos' | 'neg' | 'neu';

const toneClass: Record<Tone, string> = {
    pos: 'text-green-400',
    neg: 'text-red-400',
    neu: 'text-amber-400'
};

const Row = ({
    label,
    value,
    tone
}: {
    label: string;
    value: string;
    tone?: Tone;
}) => (
    <div className="grid grid-cols-2 gap-2 py-0.5 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span
            className={cn(
                'text-right font-mono',
                tone ? toneClass[tone] : 'text-foreground'
            )}
        >
            {value}
        </span>
    </div>
);

const Section = ({
    title,
    children
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <div>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
        </span>
        <div className="mt-1">{children}</div>
    </div>
);

export function PointDetailsPanel({
    track,
    workout,
    progress
}: PointDetailsPanelProps) {
    if (!track || !workout) {
        return (
            <Card>
                <CardContent className="text-center text-xs text-muted-foreground">
                    Select a workout to inspect points.
                </CardContent>
            </Card>
        );
    }

    const n = track.points.length;
    const idx = Math.min(n - 1, Math.max(0, Math.floor(progress * (n - 1))));
    const p = track.points[idx];
    const prev = idx > 0 ? track.points[idx - 1] : null;
    const next = idx < n - 1 ? track.points[idx + 1] : null;
    const firstT = track.points[0]?.t ?? null;
    const tFromStart =
        p.t !== null && firstT !== null ? (p.t - firstT) / 1000 : null;

    const acc = p.acceleration;
    const accTone: Tone | undefined =
        acc === null
            ? undefined
            : acc > 0.1
              ? 'pos'
              : acc < -0.1
                ? 'neg'
                : 'neu';
    const accG = acc !== null ? acc / 9.80665 : null;

    const forwardAccel = (() => {
        if (!next) return null;
        const nextS = next.speed !== null ? next.speed : next.computedSpeed;
        const curS = p.speed !== null ? p.speed : p.computedSpeed;
        if (
            nextS === null ||
            curS === null ||
            next.dt === null ||
            next.dt === 0
        )
            return null;
        return (nextS - curS) / next.dt;
    })();

    const extraEntries = Object.entries(p.extra);

    return (
        <Card className="w-full">
            <CardContent className="flex flex-col gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <span
                            className="size-2.5 rounded-full"
                            style={{ backgroundColor: workout.color }}
                        />
                        <span className="text-xs font-semibold">
                            {workout.label}
                        </span>
                    </div>
                    <span className="mt-1 block text-xs text-muted-foreground">
                        Index {idx + 1} of {n}
                    </span>
                </div>

                <Separator />

                <Section title="Time">
                    <Row label="Time (UTC)" value={fmtTime(p.t)} />
                    <Row label="From start" value={fmtDuration(tFromStart)} />
                    {prev && (
                        <Row label="Δt from prev" value={`${fmt(p.dt, 3)} s`} />
                    )}
                </Section>

                <Section title="Position">
                    <Row label="Latitude" value={`${fmt(p.lat, 7)}°`} />
                    <Row label="Longitude" value={`${fmt(p.lon, 7)}°`} />
                    <Row
                        label="Elevation"
                        value={p.ele !== null ? `${fmt(p.ele, 2)} m` : '—'}
                    />
                    <Row label="Cumulative" value={`${fmt(p.cumDist, 2)} m`} />
                    <Row label="Segment" value={`${fmt(p.segDist, 3)} m`} />
                </Section>

                <Section title="Speed">
                    <Row
                        label="Reported"
                        value={
                            p.speed !== null ? `${fmtKmh(p.speed)} km/h` : '—'
                        }
                    />
                    <Row
                        label="Raw"
                        value={
                            p.speed !== null ? `${fmt(p.speed, 3)} m/s` : '—'
                        }
                    />
                    <Row
                        label="Computed"
                        value={
                            p.computedSpeed !== null
                                ? `${fmtKmh(p.computedSpeed)} km/h`
                                : '—'
                        }
                    />
                    <Row
                        label="Speed accuracy"
                        value={
                            p.speedAccuracy !== null
                                ? `±${fmt(p.speedAccuracy, 2)} m/s`
                                : '—'
                        }
                    />
                </Section>

                <Section title="Acceleration">
                    <Row
                        label="Acceleration"
                        value={acc !== null ? `${fmt(acc, 3)} m/s²` : '—'}
                        tone={accTone}
                    />
                    <Row
                        label="In g"
                        value={accG !== null ? `${fmt(accG, 3)} g` : '—'}
                        tone={accTone}
                    />
                    <Row
                        label="Forward accel"
                        value={
                            forwardAccel !== null
                                ? `${fmt(forwardAccel, 3)} m/s²`
                                : '—'
                        }
                    />
                </Section>

                <Section title="Heading">
                    <Row
                        label="Heading"
                        value={
                            p.heading !== null ? `${fmt(p.heading, 2)}°` : '—'
                        }
                    />
                    <Row
                        label="Accuracy"
                        value={
                            p.headingAccuracy !== null
                                ? `±${fmt(p.headingAccuracy, 2)}°`
                                : '—'
                        }
                    />
                    <Row
                        label="Δ heading"
                        value={
                            p.headingDelta !== null
                                ? `${fmt(p.headingDelta, 2)}°`
                                : '—'
                        }
                    />
                    <Row
                        label="Yaw rate"
                        value={
                            p.yawRate !== null
                                ? `${fmt(p.yawRate, 2)} °/s`
                                : '—'
                        }
                    />
                </Section>

                <Section title="GPS metadata">
                    <Row label="Source" value={p.src || '—'} />
                    <Row label="HDOP" value={fmt(p.hdop, 2)} />
                    <Row label="VDOP" value={fmt(p.vdop, 2)} />
                </Section>

                {extraEntries.length > 0 && (
                    <Section title="Other">
                        {extraEntries.map(([k, v]) => (
                            <Row key={k} label={k} value={v} />
                        ))}
                    </Section>
                )}
            </CardContent>
        </Card>
    );
}
